import { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import toast from 'react-hot-toast'
import OtpInput from 'react-otp-input'
import Modal from '../../Unknown/Modal'
import translate from '../../../i18n/translate'
import { useAppDispatch, useAppSelector } from '../../../modules/store/hooks'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import { eventApi } from '../../../modules/services/eventService'

type UserOption = { id: number; name: string; phone: string }

type EventInfo = {
  id: number
  name: string
  creator?: { id: number; name?: string; surname?: string; phone?: string }
}

const SHORT_LINK_REGEX = /^[A-Za-z0-9_-]{11}$/
const SHORT_LINK_IN_URL_REGEX = /\/e\/([A-Za-z0-9_-]{11})(?:[/?#]|$)/
const EVENT_ID_IN_URL_REGEX = /event-(\d+)(?:[/?#]|$)/

function validateEventLinkInput(raw: string): { valid: boolean; error: string | null } {
  const value = (raw || '').trim()

  if (!value) return { valid: false, error: 'changeOwnerByLinkFieldRequiredError' }

  if (/^\d+$/.test(value)) {
    if (Number(value) <= 0) return { valid: false, error: 'changeOwnerByLinkFieldInvalidIdError' }
    return { valid: true, error: null }
  }

  if (SHORT_LINK_REGEX.test(value)) return { valid: true, error: null }

  if (SHORT_LINK_IN_URL_REGEX.test(value)) return { valid: true, error: null }

  if (EVENT_ID_IN_URL_REGEX.test(value)) return { valid: true, error: null }

  return { valid: false, error: 'changeOwnerByLinkFieldInvalidFormatError' }
}

export default function ChangeOwnerByLinkModal() {
  const dispatch = useAppDispatch()
  const { toggleChangeOwnerByLinkModal } = modalsSlice.actions
  const visible = useAppSelector((state) => state.modalsReducer.changeOwnerByLink)

  const [linkInput, setLinkInput] = useState('')
  const [linkTouched, setLinkTouched] = useState(false)
  const [event, setEvent] = useState<EventInfo | null>(null)

  const linkValidation = useMemo(() => validateEventLinkInput(linkInput), [linkInput])

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  const [keepOldOwnerAsEditor, setKeepOldOwnerAsEditor] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [code, setCode] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350)
    return () => clearTimeout(t)
  }, [search])

  const [lookupEventByLink, { isFetching: isLookingUp }] = eventApi.useLazyLookupEventByLinkQuery()

  const skipSearch = !debouncedSearch || debouncedSearch.trim().length < 2 || visible !== 'open' || !event
  const { data: usersData = [], isFetching: isSearching } = eventApi.useAdminSearchUsersQuery(
    { search: debouncedSearch.trim() },
    { skip: skipSearch }
  )

  const [sendChangeEventOwnerOtp, { isLoading: isSendingOtp }] = eventApi.useSendChangeEventOwnerOtpMutation()
  const [changeEventOwner, { isLoading: isChanging }] = eventApi.useChangeEventOwnerMutation()

  const options = useMemo<UserOption[]>(() => {
    return (usersData || []).filter((u) => u.id !== event?.creator?.id)
  }, [usersData, event?.creator?.id])

  const resetAll = () => {
    setLinkInput('')
    setLinkTouched(false)
    setEvent(null)
    setSearch('')
    setDebouncedSearch('')
    setSelectedUser(null)
    setKeepOldOwnerAsEditor(false)
    setOtpSent(false)
    setCode('')
  }

  const onCloseModal = () => {
    resetAll()
    dispatch(toggleChangeOwnerByLinkModal())
  }

  const extractErrorMessage = (res: any): string | null => {
    const data = res?.data
    if (data?.message?.message) return String(data.message.message)
    if (typeof data?.message === 'string') return data.message
    const errData = res?.error?.data
    if (errData?.message?.message) return String(errData.message.message)
    if (typeof errData?.message === 'string') return errData.message
    return null
  }

  const onLookup = async () => {
    setLinkTouched(true)
    if (!linkValidation.valid) return

    const res: any = await lookupEventByLink({ link: linkInput.trim() })

    const errorMessage = extractErrorMessage(res)
    if (errorMessage || !res?.data?.id) {
      toast.error(errorMessage || translate('changeOwnerByLinkNotFound'))
      return
    }

    setEvent(res.data as EventInfo)
  }

  const onSendOtp = async () => {
    if (!event || !selectedUser) return

    const res: any = await sendChangeEventOwnerOtp({
      id: event.id,
      newOwnerId: selectedUser.id,
      keepOldOwnerAsEditor,
    })

    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    setOtpSent(true)
    setCode('')
    toast.success(translate('changeEventOwnerOtpSent'))
  }

  const onConfirm = async (finalCode: string) => {
    if (!event || !selectedUser) return

    const res: any = await changeEventOwner({
      id: event.id,
      newOwnerId: selectedUser.id,
      keepOldOwnerAsEditor,
      code: finalCode,
    })

    const errorMessage = extractErrorMessage(res)
    if (errorMessage) {
      toast.error(errorMessage)
      return
    }

    onCloseModal()
    toast.success(translate('changeEventOwnerSuccess'))
  }

  const onOtpChange = (nextOtp: string) => {
    setCode(nextOtp)
    if (nextOtp.length === 6 && !isChanging) onConfirm(nextOtp)
  }

  const currentOwnerLabel = event?.creator
    ? `${event.creator.name || ''} ${event.creator.surname || ''} (${event.creator.phone || ''})`.trim()
    : ''

  return (
    <Modal
      title={translate('changeOwnerByLinkModalTitle')}
      visible={visible === 'open'}
      style={{ maxWidth: '560px' }}
      setVisible={onCloseModal}
    >
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" gap={1} alignItems="flex-start" width="100%">
          <TextField
            fullWidth
            size="small"
            color="warning"
            variant="outlined"
            label={translate('changeOwnerByLinkFieldLabel')}
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            onBlur={() => setLinkTouched(true)}
            disabled={otpSent}
            error={linkTouched && !linkValidation.valid}
            helperText={
              linkTouched && !linkValidation.valid && linkValidation.error
                ? translate(linkValidation.error)
                : translate('changeOwnerByLinkFieldHelper')
            }
          />
          <Button
            color="warning"
            variant="contained"
            disabled={!linkValidation.valid || isLookingUp || otpSent}
            onClick={onLookup}
            sx={{ mt: '-1px', width: '140px', height: '40px' }}
          >
            {isLookingUp ? <CircularProgress size={20} /> : translate('changeOwnerByLinkLookup')}
          </Button>
        </Box>

        {event && (
          <>
            <Box>
              <Typography variant="body2" color="textSecondary">
                Event:
              </Typography>
              <Typography>
                <b>[{event.id}] {event.name}</b>
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="textSecondary">
                {translate('changeEventOwnerCurrentOwner')}:
              </Typography>
              <Typography>
                <b>{currentOwnerLabel || '-'}</b>
              </Typography>
            </Box>

            <Autocomplete<UserOption>
              disabled={otpSent}
              value={selectedUser}
              onChange={(_, next) => setSelectedUser(next)}
              inputValue={search}
              onInputChange={(_, next) => setSearch(next)}
              options={options}
              filterOptions={(x) => x}
              getOptionLabel={(option) => `${option.name || ''} (${option.phone || ''}) [id ${option.id}]`.trim()}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              loading={isSearching}
              noOptionsText={translate('changeEventOwnerNoOwnerSelected')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={translate('changeEventOwnerSearchLabel')}
                  color="warning"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="warning" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <FormControlLabel
              disabled={otpSent}
              control={
                <Switch
                  color="warning"
                  checked={keepOldOwnerAsEditor}
                  onChange={(e) => setKeepOldOwnerAsEditor(e.target.checked)}
                />
              }
              label={translate('changeEventOwnerKeepOldOwner')}
            />

            {!otpSent && (
              <Button
                color="warning"
                variant="contained"
                disabled={!selectedUser || isSendingOtp}
                onClick={onSendOtp}
              >
                {isSendingOtp ? <CircularProgress size={20} /> : translate('changeEventOwnerSendOtp')}
              </Button>
            )}

            {otpSent && (
              <Box display="flex" flexDirection="column" gap={1} alignItems="center">
                <Typography variant="body2">
                  {translate('changeEventOwnerOtpLabel')}
                </Typography>
                <OtpInput
                  value={code}
                  onChange={onOtpChange}
                  inputStyle={{ width: '40px', height: '40px', color: 'black', fontSize: '18px' }}
                  containerStyle={{ display: 'flex', justifyContent: 'center' }}
                  separator={<span style={{ padding: '5px', fontSize: '18px' }}>-</span>}
                  isInputNum
                  numInputs={6}
                />

                <Box display="flex" gap={1}>
                  <Button color="warning" variant="outlined" disabled={isSendingOtp} onClick={onSendOtp}>
                    {translate('changeEventOwnerResendOtp')}
                  </Button>
                  <Button
                    color="warning"
                    variant="contained"
                    disabled={code.length !== 6 || isChanging}
                    onClick={() => onConfirm(code)}
                  >
                    {isChanging ? <CircularProgress size={20} /> : translate('changeEventOwnerConfirm')}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}
